import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { IconContext } from 'react-icons'
import { IoMdCheckmark, IoMdClose, IoMdRefresh } from 'react-icons/io'
import CsvDownloader from 'react-csv-downloader'
import AnimatedLogo from '@wellbeingxltd/fe-lib/images/dist/logo/AnimatedLogo.gif'
import _ from 'lodash'
import { GET_STORE } from '@wellbeingxltd/fe-lib/constants'
import { getSupplierOrderTemplateBody, getSupplierOrderTemplateHeader, listOfSupplierOrderTemplates } from '@wellbeingxltd/fe-lib/helpers'
import { AnalyticsActions } from 'store/models/analytics'
import TransactionsActions from 'store/models/transactions'
import TicketsActions from 'store/models/tickets'
import { Button, Label } from 'components'
import TransactionTable from './TransactionTable'
import './style.scss'

const Transactions = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const [supplier, __] = useState(JSON.parse(localStorage.getItem('supplier')))
    const { loadingTransactions, pendingTransactions, transactions, transactionsToExport } = useSelector(GET_STORE.TRANSACTIONS)
    const { loadingTickets, tickets } = useSelector(GET_STORE.TICKETS)

    const [exportFormat, changeExportFormat] = useState('')
    const [showExportModal, setShowExportModal] = useState(false)
    const [showHelpModal, setShowHelpModal] = useState(false)

    useEffect(() => {
        document.title = 'Transactions | Betterspace Supplier'
        if (_.isEmpty(transactions)) {
            const { supplierId } = supplier
            dispatch(TransactionsActions.getTransactions(supplierId))
        }
        if (_.isEmpty(tickets)) {
            dispatch(TicketsActions.getTickets())
        }
        dispatch(AnalyticsActions.postAnalytics('[Transactions] componentDidMount', null))
    }, [])

    if (loadingTransactions || loadingTickets) {
        return (
            <div className="centerLoader">
                <img src={AnimatedLogo} width="128px" alt="Full screen loading spinner" />
            </div>
        )
    }
    if (!transactions) {
        return null
    }

    return (
        <section>
            <div className="supplierTransactionsHeader">
                <div className="supplierTransactionsHeaderLeft">
                    <h1>Orders for {supplier.companyName}</h1>
                    {pendingTransactions ? (
                        <div className="supplierTransactionsHeaderBubble">{pendingTransactions} Pending</div>
                    ) : (
                        <div className="supplierTransactionsHeaderBubble supplierTransactionsHeaderBubble--done">
                            <IconContext.Provider value={{ className: 'supplierTransactionsCheckmarkIcon' }}>
                                <IoMdCheckmark />
                            </IconContext.Provider>
                            All dispatched
                        </div>
                    )}
                </div>
                <div className="supplierTransactionsHeaderRight">
                    <div className="supplierTransactionsExportButton" onClick={() => setShowExportModal(true)}>
                        EXPORT CSV
                    </div>
                    <div
                        className="supplierTransactionsRefreshButton"
                        onClick={() => {
                            dispatch(TransactionsActions.getTransactions(supplier.supplierId))
                            dispatch(TicketsActions.getTickets())
                        }}
                        title="Refresh"
                    >
                        <IconContext.Provider value={{ className: 'supplierTransactionsRefreshIcon' }}>
                            <IoMdRefresh />
                        </IconContext.Provider>
                    </div>
                    <div className="supplierTransactionsHelpButton" onClick={() => setShowHelpModal(true)} title="Help">
                        ?
                    </div>
                </div>
            </div>

            <div className="supplierTransactionsSubheader">
                <p>
                    User/Order queries: Click "Email user" under specific order, this will inform the user and Betterspace support of your query.{' '}
                    <br /> Any other query: <a href="mailto:supplier.management@betterspace.uk">supplier.management@betterspace.uk</a>
                </p>
            </div>

            <TransactionTable
                load={transaction => {
                    dispatch(TransactionsActions.openTransaction(transaction))
                    history.push(`/transaction/${transaction.transactionId}`)
                }}
                toggleTransactionToExport={transaction => dispatch(TransactionsActions.selectTransaction(transaction))}
            />

            <div className={`supplierTransactionsModal ${showExportModal && 'supplierTransactionsModal--show'}`}>
                <div className="supplierTransactionsModalContents">
                    <h2>Exporting {transactionsToExport.length} orders to CSV</h2>

                    {!transactionsToExport.length && (
                        <p>
                            You haven't selected any orders to export - use the checkboxes on the left or the buttons below to select some orders
                            first.
                        </p>
                    )}

                    <div className="supplierTransactionsExportButtons">
                        <div className="supplierTransactionsExportButton" onClick={() => dispatch(TransactionsActions.selectAllTransactions())}>
                            SELECT ALL
                        </div>
                        <div className="supplierTransactionsExportButton" onClick={() => dispatch(TransactionsActions.selectPendingTransactions())}>
                            SELECT PENDING
                        </div>
                    </div>

                    {!!transactionsToExport.length && (
                        <>
                            <Label>Export format</Label>
                            <select
                                defaultValue="default"
                                onChange={val => {
                                    changeExportFormat(val.target[val.target.selectedIndex].text)
                                }}
                            >
                                <option value="default" disabled>
                                    ---
                                </option>
                                {listOfSupplierOrderTemplates.map(opt => (
                                    <option>{opt}</option>
                                ))}
                            </select>

                            {exportFormat !== '' && (
                                <CsvDownloader
                                    columns={getSupplierOrderTemplateHeader(exportFormat)}
                                    datas={getSupplierOrderTemplateBody(exportFormat, transactionsToExport)}
                                    filename={`${
                                        supplier.companyName ? supplier.companyName.replace(/([^a-z0-9_-]+)/gi, '') : ''
                                    }-Transactions-${moment().format('YYYYMMDDHHmmss')}`}
                                    newLineAtEnd={true}
                                    wrapColumnChar={'"'}
                                >
                                    <Button cta>Export</Button>
                                </CsvDownloader>
                            )}
                        </>
                    )}
                </div>
                <div className="supplierTransactionsModalCloseButton" onClick={() => setShowExportModal(false)}>
                    <IconContext.Provider value={{ className: 'supplierTransactionsModalCloseButtonIcon' }}>
                        <IoMdClose />
                    </IconContext.Provider>
                </div>
            </div>

            <div className={`supplierTransactionsModal ${showHelpModal && 'supplierTransactionsModal--show'}`}>
                <div className="supplierTransactionsModalContents">
                    <h2>Help</h2>

                    <div className="supplierTransactionsModalGrid">
                        <div>
                            <p className="supplierTransactionsModalHeading">FULFILMENT (in-platform)</p>
                            <ol>
                                <li>This page displays your inbound orders with the most recent at the top.</li>
                                <li>Click the product name on the left to start processing an order.</li>
                                <li>Check the order details in section 1.</li>
                                <li>Find the shipping details in section 2.</li>
                                <li>Enter the tracking details in section 3 and then mark the order as dispatched.</li>
                                <li>We'll send the user a confirmation email automatically.</li>
                            </ol>
                        </div>
                        <div>
                            <p className="supplierTransactionsModalHeading">FULFILMENT (external)</p>
                            <ol>
                                <li>Click the EXPORT CSV button in the top-right to begin exporting your orders.</li>
                                <li>
                                    Select the orders you wish to export by using the buttons or individually using the checkboxes to the left of each
                                    order, then select the format you wish to export for.
                                </li>
                                <li>Process your orders using your own fulfilment system.</li>
                                <li>
                                    Follow the in-platform fulfilment steps (left) to mark the processed orders as dispatched and enter the tracking
                                    details.
                                </li>
                                <li>We'll send the user a confirmation email automatically.</li>
                            </ol>
                        </div>
                        <div>
                            <p className="supplierTransactionsModalHeading">RETURNS</p>
                            <ol>
                                <li>Click the product name on the left for the order which has been returned.</li>
                                <li>Click "mark it as returned/cancelled" at the bottom of the page and confirm.</li>
                                <li>We'll refund the user automatically.</li>
                            </ol>
                        </div>
                    </div>
                </div>
                <div className="supplierTransactionsModalCloseButton" onClick={() => setShowHelpModal(false)}>
                    <IconContext.Provider value={{ className: 'supplierTransactionsModalCloseButtonIcon' }}>
                        <IoMdClose />
                    </IconContext.Provider>
                </div>
            </div>
        </section>
    )
}

export default Transactions
